import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Image, Layout, notification, PageHeader, Popconfirm, Row, Space, Table, Tag, Tooltip } from "antd";
import { useApplicationContext } from "components/Application";
import React, { Fragment } from "react";
import { useNavigate, useParams } from "react-router";
import { NavLink } from "react-router-dom";

const articleItemQuery = gql`
  query ($id: ID!) {
    articleItem(id: $id) {
      id
      variants {
        id
        code
        nameEn
        cover
        available
        color {
          id
          code
          nameEn
        }
        externalComposition {
          id
          code
        }
      }
    }
  }
`;

const deleteArticleItemMutation = gql`
  mutation ($id: ID!) {
    deleteArticleItem(id: $id) {
      item {
        id
        code
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const variables = { id };
  const { loading, data, refetch } = useQuery(articleItemQuery, { variables, fetchPolicy: "cache-and-network" });

  const handleTableChange = () => {};

  const [execDelete] = useMutation(deleteArticleItemMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      notification.error({
        placement: "bottomRight",
        message: "Cannot delete this Item",
      });
    },
    onCompleted: ({ deleteArticleItem: { item } }) => {
      notification.success({
        placement: "bottomRight",
        message: `Item "${item.code}" deleted`,
      });
      refetch();
    },
  });

  const columns = [
    {
      title: "Status",
      dataIndex: "available",
      render: (value: boolean) =>
        value ? (
          <Tag color="green">√</Tag>
        ) : (
          <Tag color="red">X</Tag>
        ),
    },
    {
      title: "Cover",
      dataIndex: "cover",
      render: (value: string) => <Image src={`${appEndpointStore}${value}`} width={50} />,
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (value: string, resource: any) => <NavLink to={`../../${resource.id}`}>{value}</NavLink>,
    },
    {
      title: "Name",
      dataIndex: "nameEn",
    },
    {
      title: "Ext.Comp.",
      render: (resource: any) => <NavLink to={`/global/attributes/compositions/${resource.externalComposition.id}/edit`}>{resource.externalComposition.code}</NavLink>,
    },
    {
      title: "Color",
      dataIndex: "color",
      render: (color: any) => <NavLink target="_blank" to={`/global/attributes/colors/${color.id}/edit`}>{color.code} - {color.nameEn}</NavLink>,
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (id: string) => (
        <Row justify="end">
          <Space>
            <Tooltip title="Edit" placement="bottom">
              <Button onClick={() => navigate(`../../${id}/edit`)} icon={<EditOutlined />}></Button>
            </Tooltip>
            <Popconfirm title="Are you sure?" icon={null} onConfirm={() => execDelete({ variables: { id } })}>
              <Tooltip title="Delete" placement="bottom">
                <Button danger icon={<DeleteOutlined />}></Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <PageHeader
        ghost={true}
        title="Variants"
        extra={
          <Button onClick={() => navigate("new")} type="primary">
            <PlusOutlined />
            New
          </Button>
        }
      />
      <Layout.Content className="content">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data?.articleItem?.variants}
          pagination={false}
          onChange={handleTableChange}
        />
      </Layout.Content>
    </Fragment>
  );
};
